import React, { useLayoutEffect, useRef, useState } from 'react';

import { useSetRecoilState } from 'recoil';

import { useGeneralChatThreads } from 'lib/hooks/queries/myscribeAI/useGeneralChatThreads';

import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessages } from './ChatMessages';
import { Sidebar } from './Sidebar';
import { generalChatPromptListState } from './state';

const getHorizontalPosition = (buttonRect, modalRect, position) => {
  const hasSpaceRight = buttonRect.right + modalRect.width + 6 <= window.innerWidth;
  const hasSpaceLeft = buttonRect.left - modalRect.width - 6 >= 6;
  if (position === 'right') {
    if (hasSpaceRight) {
      return { left: buttonRect.right + 6, right: 'unset', placement: 'right' };
    } else if (hasSpaceLeft) {
      return { left: buttonRect.left - modalRect.width - 6, right: 'unset', placement: 'left' };
    }
    return { left: 6, right: 'unset', placement: 'left-pinned' };
  }
  if (hasSpaceLeft) {
    return { left: 'unset', right: window.innerWidth - buttonRect.right + 6, placement: 'left' };
  } else if (hasSpaceRight) {
    return { left: buttonRect.right + 6, right: 'unset', placement: 'right' };
  }
  return { left: 6, right: 'unset', placement: 'right-pinned' };
};

const calculateModalPosition = ({ buttonRect, modalRect, position = 'right' }) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Space constraints
  const hasSpaceRight = buttonRect.right + modalRect.width + 6 <= viewportWidth;
  const hasSpaceLeft = buttonRect.left - modalRect.width - 6 >= 6;
  const hasHorizontalSpace = hasSpaceRight || hasSpaceLeft;

  const hasSpaceBottom = buttonRect.bottom + modalRect.height + 6 <= viewportHeight;
  const hasSpaceTop = buttonRect.top - modalRect.height - 6 >= 6;

  let top,
    bottom,
    placement = '';

  // Case 1: No vertical space, center vertically
  if (!hasSpaceTop && !hasSpaceBottom && hasHorizontalSpace) {
    top = Math.max(6, (viewportHeight - modalRect.height) / 2);
    bottom = viewportHeight - top - modalRect.height;
    placement = 'center-vertically';
  }
  // Case 2: No bottom space, show above
  else if (!hasSpaceBottom) {
    top = buttonRect.top - modalRect.height - 6;
    if (top < 6) {
      if (hasSpaceBottom) {
        top = buttonRect.bottom + 6; // Flip to below
        placement = 'below-flipped';
      } else {
        top = 6; // Pin to top
        placement = 'above-pinned';
      }
    } else {
      placement = 'above';
    }
    bottom = viewportHeight - top - modalRect.height;
  }
  // Case 3: Default, show below
  else {
    top = buttonRect.bottom + 6;
    bottom = viewportHeight - top - modalRect.height;
    placement = 'below';
  }

  // Horizontal position
  const horizontal = getHorizontalPosition(buttonRect, modalRect, position);

  return {
    position: 'fixed',
    bottom: `${bottom}px`,
    left: horizontal.left,
    right: horizontal.right,
    placement: `${placement}-${horizontal.placement}`
  };
};

export const ChatModal = ({
  toggleModal,
  title,
  buttonPosition,
  buttonRef,
  position = 'right'
}) => {
  const modalRef = useRef(null);
  const [modalStyle, setModalStyle] = useState({ opacity: 0 }); // Hide initially
  const [isMounted, setIsMounted] = useState(false);

  const [isExpanded, setIsExpanded] = useState(false);
  const setGeneralChatPromptList = useSetRecoilState(generalChatPromptListState);

  useLayoutEffect(() => {
    const modal = modalRef.current;
    const button = buttonRef.current;
    if (!modal || !button) {
      console.log('Missing refs:', { modal: !!modal, button: !!button });
      return;
    }

    // Mark as mounted
    setIsMounted(true);

    const updatePosition = () => {
      const modalRect = modal.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(modal);

      const newPosition = calculateModalPosition({
        buttonRect,
        modalRect,
        position
      });

      console.log('Modal Positioning:', {
        position,
        isExpanded,
        isMounted,
        buttonPosition,
        buttonRect: {
          left: buttonRect.left,
          top: buttonRect.top,
          bottom: buttonRect.bottom,
          right: buttonRect.right,
          width: buttonRect.width,
          height: buttonRect.height
        },
        modalRect: { width: modalRect.width, height: modalRect.height },
        computedStyle: { width: computedStyle.width, height: computedStyle.height },
        calculatedPosition: newPosition,
        expectedTop: buttonRect.top - modalRect.height - 6,
        placement: newPosition.placement,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        space: {
          hasSpaceRight: buttonRect.right + modalRect.width + 6 <= window.innerWidth,
          hasSpaceLeft: buttonRect.left - modalRect.width - 6 >= 6,
          hasSpaceBottom: buttonRect.bottom + modalRect.height + 6 <= window.innerHeight,
          hasSpaceTop: buttonRect.top - modalRect.height - 6 >= 6
        }
      });

      setModalStyle({
        left: newPosition.left,
        right: newPosition.right,
        bottom: newPosition.bottom,
        position: newPosition.position,
        opacity: 1 // Show after positioning
      });
    };

    // Use requestAnimationFrame for smooth rendering
    const raf = requestAnimationFrame(updatePosition);

    return () => cancelAnimationFrame(raf);
  }, [buttonPosition, isExpanded, buttonRef, position]);

  const toggleSize = () => setIsExpanded(!isExpanded);

  useGeneralChatThreads({
    params: {
      kind: 'general_chat'
    },
    options: {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setGeneralChatPromptList(data?.threads || []);
      }
    }
  });

  return (
    <div className="fixed z-50">
      <div className="fixed inset-0 bg-black opacity-10" onClick={toggleModal}></div>
      <div
        ref={modalRef}
        className={`fixed rounded-md bg-white shadow-xl duration-300 ${
          isExpanded
            ? 'h-[75vh] max-h-[900px] w-1/2 sm:w-3/4 lg:w-2/3'
            : 'h-[60vh] max-h-[700px] w-[500px] sm:w-96'
        }`}
        style={modalStyle}>
        <div className="flex h-full">
          {isExpanded && <Sidebar />}
          <div className={`${isExpanded ? 'w-3/4' : 'w-full'} flex h-full flex-col`}>
            <ChatHeader
              title={title}
              isExpanded={isExpanded}
              toggleSize={toggleSize}
              toggleModal={toggleModal}
            />
            <ChatMessages />
            <ChatInput />
          </div>
        </div>
      </div>
    </div>
  );
};
